import { useEffect } from "react";
import { useMotionValue, type MotionValue } from "framer-motion";

import { NavMotion } from "@/blueprint/tokens/Motion";

/**
 * How far the pill has taken over from the in-flow row, 0 to 1.
 *
 * Driven by where the row actually is rather than by a scroll threshold and a
 * timed fade. The row does not fade out — it scrolls off — so anything that
 * starts the pill's entrance when the row has already left leaves a hole the
 * length of the transition. Reading the row's own bottom edge and finishing
 * the pill's entrance on the frame that edge crosses the viewport top closes
 * the hole by construction, at any scroll speed, and reverses for free on the
 * way back up.
 *
 * `forced` is for pages that render the pill regardless of scroll; reduced
 * motion snaps the same value rather than ramping it.
 */
export function useNavHandoff(
  navRef: { current: HTMLElement | null },
  { forced = false, reducedMotion = false } = {},
): MotionValue<number> {
  const progress = useMotionValue(forced ? 1 : 0);

  useEffect(() => {
    if (forced) {
      progress.set(1);
      return;
    }

    const nav = navRef.current;
    if (nav === null) return;

    const { distancePx, leadPx } = NavMotion.handoff;
    // The row's bottom in document space. Read with the current scroll added
    // back in so a measure taken mid-page is worth the same as one at the top.
    let bottomInDocument = 0;

    const measure = () => {
      bottomInDocument = nav.getBoundingClientRect().bottom + window.scrollY;
    };

    const update = () => {
      const travelled = window.scrollY - (bottomInDocument - distancePx);
      const ramp = Math.min(1, Math.max(0, travelled / (distancePx - leadPx)));
      progress.set(reducedMotion ? (ramp >= 1 ? 1 : 0) : ramp);
    };

    const remeasure = () => {
      measure();
      update();
    };

    remeasure();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", remeasure);
    // The announcement bar can close and the row can change height under it,
    // either of which moves the edge this is measured against.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(remeasure);
    observer?.observe(nav);
    observer?.observe(document.documentElement);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", remeasure);
      observer?.disconnect();
    };
  }, [navRef, progress, forced, reducedMotion]);

  return progress;
}
